<script setup lang="ts">
import { logApiError } from '~/utils/logger'

const props = withDefaults(defineProps<{
  showMagicLink?: boolean
  magicLinkLoading?: boolean
  magicLinkDisabled?: boolean
}>(), {
  showMagicLink: false,
  magicLinkLoading: false,
  magicLinkDisabled: false,
})

const emit = defineEmits<{
  magicLinkClick: []
}>()

const attrs = useAttrs()

const { signInWithGoogle, signInWithMicrosoft } = useSupabaseAuth()
const route = useRoute()

const getReturnPath = () => {
  const queryReturn = typeof route.query.to === 'string' ? route.query.to : undefined
  const isAuthPage = route.meta.unauthenticatedOnly === true

  return queryReturn || (isAuthPage ? '/' : route.fullPath)
}

const handleGoogleSignIn = async () => {
  try {
    await signInWithGoogle(getReturnPath())
  }
  catch (error) {
    logApiError('/auth/google-sign-in', error, { returnPath: getReturnPath() })
  }
}

const handleMicrosoftSignIn = async () => {
  try {
    await signInWithMicrosoft(getReturnPath())
  }
  catch (error) {
    logApiError('/auth/microsoft-sign-in', error, { returnPath: getReturnPath() })
  }
}

const handleMagicLink = () => {
  if ('onMagicLinkClick' in attrs) {
    emit('magicLinkClick')
  }
  else {
    navigateTo({ name: 'auth-magic-link' })
  }
}
</script>

<template>
  <div class="d-flex gap-4 justify-center flex-wrap">
    <VBtn
      variant="tonal"
      class="auth-provider-btn"
      @click="handleGoogleSignIn"
    >
      <VIcon
        icon="mdi-google"
        class="me-2"
      />
      Google
    </VBtn>

    <VBtn
      variant="tonal"
      class="auth-provider-btn"
      @click="handleMicrosoftSignIn"
    >
      <VIcon
        icon="bx-bxl-microsoft"
        class="me-2"
      />
      Microsoft
    </VBtn>

    <VBtn
      v-if="props.showMagicLink"
      variant="tonal"
      class="auth-provider-btn"
      :loading="props.magicLinkLoading"
      :disabled="props.magicLinkDisabled"
      @click="handleMagicLink"
    >
      <VIcon icon="bx-link-alt" class="me-2" />
      Link
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
.auth-provider-btn {
  position: relative;
  transition: all 0.3s ease;

  &::after {
    position: absolute;
    background-color: rgb(var(--v-theme-primary));
    block-size: 0;
    content: "";
    inline-size: 100%;
    inset-block-end: 0;
    inset-inline-start: 0;
    transition: block-size 0.3s ease;
  }

  &:hover::after {
    block-size: 3px;
  }
}
</style>
